// Module-level singleton shared by selection geometry + interactions. This is
// state that all sibling files in @scene/picking/ read and write — kept here
// so each concern can split cleanly without threading a context through every
// call. (Predates the @app/context ViewerContext split; could fold in later.)

import type * as THREE from 'three';
import type { ThemeDefaults } from '@config/defaults';
import type { FieldMetadata, ColorRGBA } from '@physics/types';

type ThreeNamespace = typeof import('three');

export interface PickingState {
    THREE: ThreeNamespace | null;
    scene: THREE.Scene | null;
    invisibleCubeMesh: THREE.Mesh | null;
    cubeWireframe: THREE.Group | null;
    cubeCenterDot: THREE.Mesh | null;
    selectionCube: THREE.Group | null;
    selectionRing: THREE.Group | null;
    projectionLines: THREE.Group | null;
    cubeSize: number;
    crossLength: number;
    SCENE_SELECTION_SPHERE_RADIUS: number;
    THEME: ThemeDefaults | null;
    SCENE_SELECTION_PROJECTION_COLOR: string | number | null;
    SCENE_SELECTION_PROJECTION_OPACITY: number | null;
    mapPhysicalToThreeWithRotation: ((p: THREE.Vector3) => THREE.Vector3) | null;
    threeJSToPhysical: ((x: number, y: number, z: number) => THREE.Vector3) | null;
    getFieldValue: ((x: number, y: number, z: number) => number) | null;
    fieldStrengthToColor: ((s: number) => ColorRGBA) | null;
    UNITS: string;
    fieldMetadata: FieldMetadata | null;
    getCoordUnitsFn: (() => string) | null;
    getFieldUnitsFn: (() => string) | null;
    getFieldLabelFn: (() => string) | null;
}

export const state: PickingState = {
    THREE: null,
    scene: null,
    invisibleCubeMesh: null,
    cubeWireframe: null,
    cubeCenterDot: null,
    selectionCube: null,
    selectionRing: null,
    projectionLines: null,
    cubeSize: 1.0,
    crossLength: 0.15,
    SCENE_SELECTION_SPHERE_RADIUS: 0.1,
    THEME: null,
    SCENE_SELECTION_PROJECTION_COLOR: null,
    SCENE_SELECTION_PROJECTION_OPACITY: null,
    mapPhysicalToThreeWithRotation: null,
    threeJSToPhysical: null,
    getFieldValue: null,
    fieldStrengthToColor: null,
    UNITS: '',
    fieldMetadata: null,
    getCoordUnitsFn: null,
    getFieldUnitsFn: null,
    getFieldLabelFn: null,
};

interface TooltipScratch { world: THREE.Vector3; ndc: THREE.Vector3 }

let _tooltipScratch: TooltipScratch | null = null;
export function tooltipScratch(): TooltipScratch {
    if (_tooltipScratch) return _tooltipScratch;
    const T = state.THREE!;
    _tooltipScratch = { world: new T.Vector3(), ndc: new T.Vector3() };
    return _tooltipScratch;
}
